import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import { getShippingRates, displayShippingOptions } from '../services/shippingService';

function Checkout({ cart, onCheckoutComplete, onCancel }) {
  const [shippingDetails, setShippingDetails] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
  });


  const [customerReference, setCustomerReference] = useState('');
  const [shippingRates, setShippingRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [shipEngineAPI, setShipEngineAPI] = useState(null);
  const [shippingMethod, setShippingMethod] = useState('hold'); // 'internal', 'shipengine', or 'hold'
  const [holdInstructions, setHoldInstructions] = useState('');
  const [rateError, setRateError] = useState(null);
  const [showShipEngineWarning, setShowShipEngineWarning] = useState(false);
  const [pendingShipEngineSelection, setPendingShipEngineSelection] = useState(false);
  const [fromAddress, setFromAddress] = useState({
    name: '',
    company_name: '',
    address_line1: '',
    city_locality: '',
    state_province: '',
    postal_code: '',
    country_code: '',
    phone: ''
  });
  const [deliveryDate, setDeliveryDate] = useState('');
  const [shipDate, setShipDate] = useState(() => {
    // Default to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [customerRef, setCustomerRef] = useState('');

  useEffect(() => {
    const apiKey = sessionStorage.getItem('shipEngineAPI');
    setShipEngineAPI(apiKey);
  }, []);

  useEffect(() => {
    console.log('ShipEngine API Key:', sessionStorage.getItem('shipEngineAPI'));
  }, []);

  useEffect(() => {
    // Debug what's in session storage
    const user = JSON.parse(sessionStorage.getItem('user'));
    const apiKey = sessionStorage.getItem('shipEngineAPI');
    
    console.log('User from session:', user);
    console.log('ShipEngine API from session:', apiKey);
  }, []);

  useEffect(() => {
    // Get user data including home warehouse info
    const user = JSON.parse(sessionStorage.getItem('user'));
    console.log('Loading warehouse data:', user?.homeWarehouse); // Debug log
    
    if (user?.homeWarehouse) {
      const warehouse = user.homeWarehouse;
      console.log('Setting from address with warehouse:', warehouse);
      
      setFromAddress({
        name: warehouse.name || '',
        company_name: warehouse.name || '',
        address_line1: warehouse.address?.street || '',
        city_locality: warehouse.address?.city || '',
        state_province: warehouse.address?.state || '',
        postal_code: warehouse.address?.zip || '',
        country_code: "US",
        phone: warehouse.address?.phone || ""
      });
    } else {
      // Provide default warehouse information if not available in session
      console.warn('No warehouse information found in user session, using default');
      setFromAddress({
        name: "LOWELL BLDG 1",
        company_name: "LOWELL BLDG 1",
        address_line1: "141 ROBINS ST",
        city_locality: "LOWELL",
        state_province: "NC",
        postal_code: "28098",
        country_code: "US",
        phone: "704-424-5454"
      });
    }
  }, []);

  useEffect(() => {
    // Get expDate from first cart item
    if (cart && cart.length > 0 && cart[0].expDate) {
      setDeliveryDate(cart[0].expDate);
    }
  }, [cart]);

  useEffect(() => {
    // Get shipDate from first cart item's inv_trans
    if (cart && cart.length > 0 && cart[0].shipDate) {
      setShipDate(cart[0].shipDate);
    }
  }, [cart]);

  // Add a helper function to calculate total weight
  const calculateTotalWeight = () => {
    const totalWeight = cart.reduce((sum, item) => {
      const itemWeight = parseFloat(item.weight) || 0;
      const quantity = parseInt(item.quantity) || 0;
      const totalItemWeight = itemWeight * quantity;
      console.log(`Item: ${item.itemName}, Weight per unit: ${itemWeight}lb, Quantity: ${quantity}, Total weight: ${totalItemWeight}lb`);
      return sum + totalItemWeight;
    }, 0);
    
    // Ensure minimum weight of 0.1 pounds for shipping
    const finalWeight = Math.max(totalWeight, 0.1);
    console.log('Final total order weight:', finalWeight, 'lb');
    return finalWeight;
  };

  const fetchShippingRates = async () => {
    if (!shippingDetails?.zipCode) return;

    try {
      setLoadingRates(true);
      setRateError(null);

      // Verify fromAddress is complete
      if (!fromAddress.address_line1 || !fromAddress.city_locality || 
          !fromAddress.state_province || !fromAddress.postal_code) {
        console.error('Incomplete warehouse address:', fromAddress);
        setRateError('Missing warehouse address information');
        return;
      }

      // Filter out items with quantity 0 and format items for shipping
      const shippingItems = cart
        .filter(item => (parseInt(item.quantity) || 0) > 0)
        .map(item => ({
          weight: parseFloat(item.weight) || 0,
          quantity: parseInt(item.quantity) || 0,
          dimLen: parseFloat(item.dimLen) || 12,
          dimWdt: parseFloat(item.dimWdt) || 8,
          dimHgt: parseFloat(item.dimHgt) || 6
        }));

      if (shippingItems.length === 0) {
        setRateError('No valid items in cart');
        setShippingRates([]);
        return;
      }

      const toAddress = {
        name: shippingDetails.name,
        phone: shippingDetails.phone || "",
        company_name: shippingDetails.company || "",
        address_line1: shippingDetails.address1,
        address_line2: shippingDetails.address2 || "",
        city_locality: shippingDetails.city,
        state_province: shippingDetails.state,
        postal_code: shippingDetails.zipCode,
        country_code: shippingDetails.country || "US"
      };

      // Log complete request details for verification
      console.log('Shipping rate request details:', {
        fromAddress,
        toAddress,
        items: shippingItems,
        deliveryDate,
        method: 'shipengine'
      });
      
      const rates = await getShippingRates(
        toAddress, 
        shippingItems, 
        'shipengine',
        deliveryDate,
        fromAddress // Pass fromAddress explicitly
      );
      
      console.log('Received shipping rates:', rates);
      const options = displayShippingOptions(rates);
      console.log('Processed shipping options:', options);
      
      // Store the original rates in sessionStorage
      sessionStorage.setItem('shippingRates', JSON.stringify(rates.rate_response.rates));
      
      setShippingRates(options);
    } catch (error) {
      console.error('Error in fetchShippingRates:', error);
      setRateError(error.message || 'Failed to fetch shipping rates');
      setShippingRates([]);
    } finally {
      setLoadingRates(false);
    }
  };

  const handleShippingDetailsChange = (e) => {
    setShippingDetails({ ...shippingDetails, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (shippingMethod === 'shipengine' && !selectedRate) {
      alert('Please select a shipping option');
      return;
    }
    if (shippingMethod === 'hold' && !holdInstructions.trim()) {
      alert('Please provide instructions for hold');
      return;
    }
    onCheckoutComplete({
      shippingDetails,
      customerReference,
      shippingMethod,
      selectedRate: shippingMethod === 'shipengine' ? selectedRate : null,
      holdInstructions: shippingMethod === 'hold' ? holdInstructions : null,
      cart
    });
  };

  // Update the shipping method change handler to show warning when selecting External Carrier
  const handleShippingMethodChange = async (method) => {
    console.log('Method changed to:', method);
    
    if (method === 'shipengine') {
      setPendingShipEngineSelection(true);
      setShowShipEngineWarning(true);
    } else {
      setShippingMethod(method);
      setShippingRates(null);
      setSelectedRate(null);
    }
  };

  const handleWarningConfirm = () => {
    setShowShipEngineWarning(false);
    setShippingMethod('shipengine');
    setPendingShipEngineSelection(false);
    
    // Only fetch rates if we have a complete address
    if (shippingDetails?.zipCode && shippingDetails?.city && shippingDetails?.state) {
      console.log('Fetching rates after warning confirm');
      fetchShippingRates();
    } else {
      console.log('Incomplete address:', shippingDetails);
      setShippingRates(null);
    }
  };

  const handleWarningCancel = () => {
    setShowShipEngineWarning(false);
    setPendingShipEngineSelection(false);
    setShippingMethod('hold');
  };

  const renderRates = () => {
    if (loadingRates) {
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}>
          <div className="spinner-border text-primary" role="status" style={{ marginRight: '10px' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <span>Retrieving available rates...</span>
        </div>
      );
    }

    if (rateError) {
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          backgroundColor: '#fff3f3',
          border: '1px solid #dc3545',
          borderRadius: '4px',
          color: '#dc3545'
        }}>
          {rateError}
        </div>
      );
    }

    if (!shippingRates) {
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}>
          <span>Please complete shipping address to view available rates</span>
        </div>
      );
    }

    if (shippingRates.length === 0) {
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}>
          No rates available for this address
        </div>
      );
    }

    // Sort rates by price
    const sortedRates = [...shippingRates].sort((a, b) => 
      parseFloat(a.price) - parseFloat(b.price)
    );

    return (
      <div style={{ 
        maxHeight: '300px', 
        overflowY: 'auto',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        marginTop: '10px',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ 
            position: 'sticky', 
            top: 0, 
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #dee2e6',
            zIndex: 1
          }}>
            <tr>
              <th style={{ width: '40px', padding: '12px 8px', textAlign: 'center' }}></th>
              <th style={{ width: '120px', padding: '12px 8px', textAlign: 'left' }}>Price</th>
              <th style={{ padding: '12px 8px', textAlign: 'left' }}>Service</th>
            </tr>
          </thead>
          <tbody>
            {sortedRates.map((rate, index) => (
              <tr key={rate.id} style={{ 
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                transition: 'background-color 0.2s',
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: 'rgba(0,123,255,0.05)'
                }
              }}>
                <td style={{ padding: '12px 8px', textAlign: 'center', verticalAlign: 'top' }}>
                  <input
                    type="radio"
                    name="shippingRate"
                    value={rate.id}
                    checked={selectedRate === rate.id}
                    onChange={() => setSelectedRate(rate.id)}
                    style={{ 
                      cursor: 'pointer',
                      marginTop: '4px'
                    }}
                  />
                </td>
                <td style={{ padding: '12px 8px', verticalAlign: 'top' }}>
                  <div style={{ 
                    fontSize: '1.1em', 
                    fontWeight: '600',
                    color: '#2c3e50'
                  }}>
                    ${parseFloat(rate.price).toFixed(2)}
                  </div>
                  {rate.estimated_range && (
                    <div style={{ 
                      fontSize: '0.85em', 
                      color: '#666',
                      marginTop: '4px'
                    }}>
                      Est. Range:<br/>
                      ${rate.estimated_range.low.toFixed(2)} - ${rate.estimated_range.high.toFixed(2)}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px 8px', verticalAlign: 'top' }}>
                  <div style={{ 
                    fontSize: '1.1em',
                    fontWeight: '500',
                    color: '#2c3e50'
                  }}>
                    {rate.carrier} {rate.service}
                  </div>
                  {rate.is_estimate && (
                    <div style={{ 
                      fontSize: '0.85em', 
                      color: '#666',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <i className="fas fa-info-circle" style={{ color: '#6c757d' }}></i>
                      {rate.estimate_message}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper function to determine if shipping fields are required
  const requireShippingFields = (method) => {
    return method === 'internal' || method === 'external' || method === 'shipengine';
  };

  return (
    <Container>
      <h2>Shipping Details</h2>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <h3>From:</h3>
            {fromAddress && (
              <div className="mb-4">
                <div className="fw-bold">{fromAddress.name}</div>
                <div>{fromAddress.address_line1}</div>
                <div>
                  {fromAddress.city_locality}, {fromAddress.state_province} {fromAddress.postal_code}
                </div>
                <div>Phone: {fromAddress.phone}</div>
              </div>
            )}

            <h3>Ship To:</h3>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={shippingDetails.name}
                onChange={handleShippingDetailsChange}
                required={requireShippingFields(shippingMethod)}
                placeholder="Enter your name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address1"
                value={shippingDetails.address1}
                onChange={handleShippingDetailsChange}
                required={requireShippingFields(shippingMethod)}
              />
            </Form.Group>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={shippingDetails.city}
                    onChange={handleShippingDetailsChange}
                    required={requireShippingFields(shippingMethod)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={shippingDetails.state}
                    onChange={handleShippingDetailsChange}
                    required={requireShippingFields(shippingMethod)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Zip Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipCode"
                    value={shippingDetails.zipCode}
                    onChange={handleShippingDetailsChange}
                    required={requireShippingFields(shippingMethod)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={shippingDetails.phone}
                onChange={handleShippingDetailsChange}
                required={requireShippingFields(shippingMethod)}
              />
            </Form.Group>

            <div style={{ display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <label>Customer Reference</label>
                <input
                  type="text"
                  style={{
                    width: '100%',
                    padding: '6px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #ced4da',
                    borderRadius: '4px'
                  }}
                  value={customerRef}
                  onChange={(e) => setCustomerRef(e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label>Ship Date</label>
                <input
                  type="date"
                  style={{
                    width: '100%',
                    padding: '6px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #ced4da',
                    borderRadius: '4px'
                  }}
                  value={shipDate}
                  onChange={(e) => setShipDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          </Col>
          <Col md={6}>
            <h3>Order Summary</h3>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Total Pieces</th>
                  <th>Total Weight</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{cart.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)}</td>
                  <td>
                    {cart.reduce((sum, item) => {
                      console.log('Item:', item.itemName, 'Weight:', item.weight, 'Quantity:', item.quantity); // Debug log
                      const itemWeight = parseFloat(item.weight) || 0;
                      const quantity = parseInt(item.quantity) || 0;
                      const totalItemWeight = itemWeight * quantity;
                      console.log('Total item weight:', totalItemWeight); // Debug log
                      return sum + totalItemWeight;
                    }, 0).toFixed(2)} lbs
                  </td>
                </tr>
              </tbody>
            </Table>
            <h3 style={{ marginBottom: '10px' }}>Shipping Method</h3>
            <Form.Group className="mb-3">
              <Form.Check
                type="radio"
                label="Hold for Instructions"
                name="shippingMethod"
                id="hold"
                checked={shippingMethod === 'hold'}
                onChange={() => {
                  setShippingMethod('hold');
                  setSelectedRate(null);
                }}
              />
              <Form.Check
                type="radio"
                label="Internal BOL"
                name="shippingMethod"
                id="internal"
                checked={shippingMethod === 'internal'}
                onChange={() => {
                  setShippingMethod('internal');
                  setSelectedRate(null);
                  setHoldInstructions('');
                }}
              />
              <Form.Check
                type="radio"
                label="External Carrier"
                name="shippingMethod"
                id="shipengine"
                checked={shippingMethod === 'shipengine'}
                onChange={() => {
                  console.log('External Carrier selected'); // Debug log
                  handleShippingMethodChange('shipengine');
                }}
              />
              
              {shippingMethod === 'shipengine' && (
                <div className="mt-3">
                  <h5>Rates</h5>
                  {renderRates()}
                </div>
              )}
            </Form.Group>

            {shippingMethod === 'hold' && (
              <Form.Group className="mt-3">
                <Form.Label>Instructions *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={holdInstructions}
                  onChange={(e) => setHoldInstructions(e.target.value)}
                  placeholder="Enter instructions for holding the order..."
                  required
                />
              </Form.Group>
            )}
          </Col>
        </Row>
        <div className="mt-3 d-flex gap-2">
          <Button 
            variant="secondary" 
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={
              (shippingMethod === 'shipengine' && !selectedRate) ||
              (shippingMethod === 'hold' && !holdInstructions.trim())
            }
          >
            Create Pick Order
          </Button>
        </div>
      </Form>

      <Modal 
        show={showShipEngineWarning} 
        onHide={handleWarningCancel}
        centered
      >
        <Modal.Header closeButton style={{ border: 'none' }}>
          <Modal.Title style={{ fontSize: '1.2rem' }}>External Carrier Notice</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px' }}>
          <div className="d-flex align-items-start mb-3">
            <i className="fas fa-info-circle text-primary me-3 mt-1" style={{ fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, lineHeight: '1.5' }}>
              Your selection indicates your desired service to be used for shipping. 
              Delivery times and rates are estimated and may change during order fulfillment. 
              Check your ShipEngine™ dashboard for final charges.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', padding: '0 20px 20px 20px' }}>
          <Button variant="secondary" onClick={handleWarningCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleWarningConfirm}>
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Checkout;
